/**
 * Country flag emojis for ISO-2 country codes
 *
 * Each country flag emoji is constructed using two regional indicator symbols.
 * For example, the US flag emoji 🇺🇸 is made of 🇺 (U+1F1FA) and 🇸 (U+1F1F8),
 * which correspond to the letters 'U' and 'S' in the country code.
 */

export const countryCodeToFlag = (countryCode: string): string => {
	if (!countryCode) return '🏳️'

	// Convert country code to uppercase
	const code = countryCode.toUpperCase()

	// Return if code is not valid
	if (code.length !== 2) return '🏳️'

	// Convert letters to regional indicator symbols
	// Regional indicator symbols are 127397 (0x1F1E6) higher than their corresponding ASCII uppercase letters
	const offset = 127397
	const firstChar = code.charCodeAt(0) + offset
	const secondChar = code.charCodeAt(1) + offset

	// Convert code points to characters and return the flag
	return String.fromCodePoint(firstChar) + String.fromCodePoint(secondChar)
}

// A map of common country codes to their flag emojis (pre-computed for quick reference)
export const countryFlags: Record<string, string> = {
	// North America
	US: '🇺🇸', // United States
	CA: '🇨🇦', // Canada
	MX: '🇲🇽', // Mexico

	// Europe
	GB: '🇬🇧', // United Kingdom
	UK: '🇬🇧', // United Kingdom (alias)
	DE: '🇩🇪', // Germany
	FR: '🇫🇷', // France
	IT: '🇮🇹', // Italy
	ES: '🇪🇸', // Spain
	PT: '🇵🇹', // Portugal
	NL: '🇳🇱', // Netherlands
	BE: '🇧🇪', // Belgium
	CH: '🇨🇭', // Switzerland
	AT: '🇦🇹', // Austria
	SE: '🇸🇪', // Sweden
	NO: '🇳🇴', // Norway
	DK: '🇩🇰', // Denmark
	FI: '🇫🇮', // Finland
	IE: '🇮🇪', // Ireland
	PL: '🇵🇱', // Poland
	CZ: '🇨🇿', // Czech Republic
	GR: '🇬🇷', // Greece
	HU: '🇭🇺', // Hungary
	RO: '🇷🇴', // Romania
	BG: '🇧🇬', // Bulgaria
	HR: '🇭🇷', // Croatia
	RS: '🇷🇸', // Serbia
	UA: '🇺🇦', // Ukraine
	RU: '🇷🇺', // Russia

	// Asia
	CN: '🇨🇳', // China
	JP: '🇯🇵', // Japan
	KR: '🇰🇷', // South Korea
	IN: '🇮🇳', // India
	ID: '🇮🇩', // Indonesia
	MY: '🇲🇾', // Malaysia
	SG: '🇸🇬', // Singapore
	TH: '🇹🇭', // Thailand
	VN: '🇻🇳', // Vietnam
	PH: '🇵🇭', // Philippines
	TR: '🇹🇷', // Turkey
	SA: '🇸🇦', // Saudi Arabia
	AE: '🇦🇪', // United Arab Emirates
	IL: '🇮🇱', // Israel

	// Africa
	ZA: '🇿🇦', // South Africa
	EG: '🇪🇬', // Egypt
	NG: '🇳🇬', // Nigeria
	KE: '🇰🇪', // Kenya
	MA: '🇲🇦', // Morocco
	TN: '🇹🇳', // Tunisia

	// Oceania
	AU: '🇦🇺', // Australia
	NZ: '🇳🇿', // New Zealand

	// South America
	BR: '🇧🇷', // Brazil
	AR: '🇦🇷', // Argentina
	CL: '🇨🇱', // Chile
	CO: '🇨🇴', // Colombia
	PE: '🇵🇪', // Peru
	VE: '🇻🇪', // Venezuela
	UY: '🇺🇾', // Uruguay
	PY: '🇵🇾', // Paraguay
	BO: '🇧🇴', // Bolivia
	EC: '🇪🇨', // Ecuador
}

/**
 * Get a flag emoji for a country code
 * First checks the predefined map, and if not found,
 * dynamically generates the flag emoji from the ISO-2 code
 */
export const getCountryFlag = (countryCode: string): string => {
	if (!countryCode) return '🏳️'

	const code = countryCode.toUpperCase()

	// Check if we have a pre-defined flag for this country code
	if (countryFlags[code]) {
		return countryFlags[code]
	}

	// Dynamically generate the flag if not in our predefined list
	return countryCodeToFlag(code)
}
